import { DateService } from "@app/services/date.service";
import { IUser } from "@interfaces/IUser";
import { IVehicle } from "@interfaces/IVehicle";
import { IDateQueryOptions } from "@services/accelerometerRecord.service";
import { CacheService } from "@services/cache.Service";
import { MessageDialogService } from "@services/messageDialog.service";
import { NavigationService } from "@services/navigation.service";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import { TemperatureService } from "@temperature/_services/temperature.service";
import { Subject } from "rxjs";
import { AlertReviewStatus } from "src/app/main/modules/alerts/_enums/AlertReviewStatus.enums";
import { AlertCategory } from "src/app/main/modules/alerts/_enums/alerts.enums";
import { IAlertRule, IAlertRuleRecord } from "src/app/main/modules/alerts/_models/alerts.interfaces";
import { IAlertReviewEvent } from "src/app/main/modules/alerts/_models/IAlertReviewEvent";
import { IAlertReviewHistory } from "src/app/main/modules/alerts/_models/IAlertReviewHistory";

export interface IReviewHistoryQueryOptions extends IDateQueryOptions {
    Id?: number;
    AlertRecordId?: number;
    AlertRuleId?: number;
    VehicleId?: number;
}

export interface IReviewEventQueryOptions extends IDateQueryOptions {
    Id?: number;
    AlertReviewHistoryId?: number;
    VehicleId?: number;
    UserId?: number;
}

export class AlertReviewService {

    alertReviewHistoryUpdated = new Subject<IAlertReviewHistory>();
    alertReviewHistoryUpdated$ = this.alertReviewHistoryUpdated.asObservable();

    private alertReviewHistoryCache = "alertReviewHistoryCache";
    private alertEventsCache = "alertEventsCache";

    static $inject = ["$http", "$q", "cacheService", "messageDialogService", "navigationService", "temperatureService", "translationService", "vehicleService", "dateService"];

    constructor(
        private readonly $http: angular.IHttpService,
        private readonly $q: angular.IQService,
        private readonly cacheService: CacheService,
        private readonly messageDialogService: MessageDialogService,
        private readonly navigationService: NavigationService,
        private readonly temperatureService: TemperatureService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: VehicleService,
        private readonly dateService: DateService
    ) {
        this.cacheService.createCache(this.alertReviewHistoryCache, { maxAge: 30 * 60 * 1000 });
        this.cacheService.createCache(this.alertEventsCache, { maxAge: 30 * 60 * 1000 });
    }

    emitAlertReviewHistoryUpdate(alertReviewHistory: IAlertReviewHistory): void {
        this.alertReviewHistoryUpdated.next(alertReviewHistory);
    }

    getAlertReviewHistory(queryOptions?: IReviewHistoryQueryOptions): angular.IPromise<IAlertReviewHistory[]> {
        return this.cacheService.getOrRetrieveData(this.alertReviewHistoryCache, () => this.getAlertReviewHistoryFromDatabase(queryOptions), queryOptions?.invalidateCache);
    }

    getAlertReviewHistoryFromDatabase(queryOptions?: IReviewHistoryQueryOptions): angular.IPromise<IAlertReviewHistory[]> {
        if (queryOptions.Start) {
            queryOptions.Start = new Date(queryOptions.Start).toISOString();
        }

        if (queryOptions.End) {
            queryOptions.End = new Date(queryOptions.End).toISOString();
        }
        return this.$http({
            url: `/api/alertreview/histories`,
            method: "GET",
            params: queryOptions
        }).then((response: angular.IHttpResponse<IAlertReviewHistory[]>) => {
            return response.data;
        });
    }

    getAlertReviewEvents(queryOptions: IReviewEventQueryOptions): angular.IPromise<IAlertReviewEvent[]> {

        return this.cacheService.getOrRetrieveData(this.alertEventsCache, () => this.getAlertReviewEventsFromDatabase(queryOptions), queryOptions.invalidateCache);
    }

    getAlertReviewEventsFromDatabase(queryOptions: IReviewEventQueryOptions): angular.IPromise<IAlertReviewEvent[]> {
        if (queryOptions.Start) {
            queryOptions.Start = new Date(queryOptions.Start).toISOString();
        }

        if (queryOptions.End) {
            queryOptions.End = new Date(queryOptions.End).toISOString();
        }

        return this.$http({
            url: `/api/alertreview/events`,
            method: "GET",
            params: queryOptions
        }).then((response: angular.IHttpResponse<IAlertReviewEvent[]>) => {
            return response.data;
        });
    }

    addAlertReviewEvent(event: IAlertReviewEvent): angular.IPromise<IAlertReviewEvent> {
        return this.$http({
            url: `/api/alertreview/events`,
            method: "POST",
            data: event
        }).then((response: angular.IHttpResponse<IAlertReviewEvent>) => {
            this.cacheService.addOrUpdateItem(this.alertEventsCache, response.data, (v1, v2) => v1.Id == v2.Id);
            return response.data;
        });
    }

    exportAlertDetailMethod(historyItem: IAlertReviewHistory) {
        const metadata = historyItem.AlertRecord.alertMetadata;
        if (metadata.TemperatureAlarmRecordId) {
            const alarmRecordPromise = this.temperatureService.getTemperatureAlarmRecord(metadata.TemperatureAlarmRecordId);
            const histPromise = this.temperatureService.getVehicleTemperatureOutOfBoundsHistoryByAlarmRecord(metadata.TemperatureAlarmRecordId);
            return this.$q.all([alarmRecordPromise, histPromise]).then(([alarm, history]) => {
                return `${history?.TemperatureExtreme} (${alarm?.LowerBound} - ${alarm?.UpperBound})`;
            });
        }
        else if (metadata.TemperatureProfileId && metadata.TemperatureOutOfBoundsHistoryId) {
            const profilePromise = this.temperatureService.getTemperatureProfiles().then((temperatureProfiles) => {
                return temperatureProfiles.find(profile => profile.Id === metadata.TemperatureProfileId);
            });
            const histPromise = this.temperatureService.getVehicleTemperatureOutOfBoundsHistory(historyItem.AlertRecord.alertMetadata.TemperatureOutOfBoundsHistoryId);
            return this.$q.all([profilePromise, histPromise]).then(([profile, history]) => {
                return `${history?.TemperatureExtreme} (${profile?.LowerBound} - ${profile?.UpperBound})`;
            });
        }
        return;
    }

    openIncidentDetailsDialogById(incident: IAlertReviewHistory, parentStateName = 'app.alerts.review') {
        this.navigationService.go(`${parentStateName}.view`, { incidentId: incident.Id, incident: incident });
    }

    openIncidentDetailsDialog(incident: IAlertReviewHistory, parentStateName = 'app.alerts.review') {
        this.openIncidentDetailsDialogById(incident, parentStateName);
    }

    mapVehicleToHistory(historyItem: IAlertReviewHistory, vehicles: IVehicle[]): void {
        historyItem.Vehicle = this.vehicleService.findVehicle(vehicles, historyItem.VehicleId);
    }

    mapEventsToHistory(historyItem: IAlertReviewHistory, events: IAlertReviewEvent[]): void {
        historyItem.AlertReviewEvents = events.filter(event => {
            return event.AlertReviewHistoryId === historyItem.Id;
        });
        historyItem.AlertReviewEvents.sort((a, b) => {
            return new Date(a.CreatedTime).getTime() - new Date(b.CreatedTime).getTime();
        });
    }


    mapAlertRecordToHistory(historyItem: IAlertReviewHistory, records: IAlertRuleRecord[]): void {
        historyItem.AlertRecord = records.find(record => {
            return record.Id === historyItem.AlertRecordId;
        });
    }

    mapUsersToEvents(historyItem: IAlertReviewHistory, users: IUser[]) {
        if (!historyItem.AlertReviewEvents) {
            return;
        }
        historyItem.AlertReviewEvents.forEach(event => {
            if (!event.UserId) {
                return;
            }
            const eventUser = users.find(u => {
                return u.Id === event.UserId;
            });
            if (eventUser) {
                event.User = eventUser;
                return;
            }
        });
    }

    getAlertRuleCategory(historyItem: IAlertReviewHistory, alertRules: IAlertRule[]): AlertCategory {
        return alertRules.find(rule => rule.Id === historyItem.AlertRuleId).Category;
    }

    getAlertRuleSubCategory(historyItem: IAlertReviewHistory, alertRules: IAlertRule[]): any {
        return alertRules.find(rule => rule.Id === historyItem.AlertRuleId).SubCategory;
    }

    setMostRecentEvent(historyItem: IAlertReviewHistory): IAlertReviewEvent {
        if (historyItem.LatestAlertReviewEvent && !historyItem.AlertReviewEvents.length) {

            historyItem.latestAlertReviewEvent = historyItem.LatestAlertReviewEvent;

            return historyItem.latestAlertReviewEvent;
        }

        if (!historyItem.AlertReviewEvents.length) {
            return;
        }

        if (historyItem.LatestAlertReviewEventId) {
            historyItem.latestAlertReviewEvent = historyItem.AlertReviewEvents.find(event => event.Id === historyItem.LatestAlertReviewEventId);

        } else {
            historyItem.latestAlertReviewEvent = historyItem.AlertReviewEvents.slice(-1)[0];
        }

        return historyItem.latestAlertReviewEvent;
    }

    getCurrentStatusOfHistoryItem(historyItem: IAlertReviewHistory): AlertReviewStatus {

        this.setMostRecentEvent(historyItem);

        if (historyItem.LatestAlertReviewEvent) {
            historyItem.currentStatus = historyItem.LatestAlertReviewEvent.Status;
        }
        else if (historyItem.latestAlertReviewEvent) {
            historyItem.currentStatus = historyItem.latestAlertReviewEvent.Status;
        }
        else {
            historyItem.currentStatus = AlertReviewStatus.Open;
        }
        return historyItem.currentStatus;
    }

    getResolvingUser(historyItem: IAlertReviewHistory) {
        if (this.getCurrentStatusOfHistoryItem(historyItem) !== AlertReviewStatus.Closed) {
            return;
        }

        const user: IUser = historyItem?.latestAlertReviewEvent?.User;

        const username = user?.CurrentAccountUserMapping?.Name ? user.CurrentAccountUserMapping.Name : this.translationService.t("Common.UnknownUser");

        return username;
    }

    getResolutionCode(historyItem: IAlertReviewHistory): number {
        this.setMostRecentEvent(historyItem);
        if (!historyItem.latestAlertReviewEvent) {
            return;
        }
        if (historyItem.latestAlertReviewEvent.ResolutionCodeId) {
            return historyItem.latestAlertReviewEvent.ResolutionCodeId;
        }

    }

    getAlertEndTime(historyItem: IAlertReviewHistory): Date | string {
        return historyItem.AlertRecord.ClosedAtDataTime;
    }


    getResolvedTime(historyItem: IAlertReviewHistory): Date | string {
        if (!historyItem.latestAlertReviewEvent) {
            this.setMostRecentEvent(historyItem);
        };
        if (this.getCurrentStatusOfHistoryItem(historyItem) !== AlertReviewStatus.Closed) {
            return null;
        }

        return historyItem.EndTime || historyItem.latestAlertReviewEvent.CreatedTime;
    }

    getResolutionComment(historyItem: IAlertReviewHistory): string {
        this.setMostRecentEvent(historyItem);
        if (this.getCurrentStatusOfHistoryItem(historyItem) !== AlertReviewStatus.Closed) {
            return null;
        }

        return historyItem.latestAlertReviewEvent.Notes;
    }

    getDurationToResolveAlertMs(historyItem: IAlertReviewHistory): number | null {
        if (historyItem.latestAlertReviewEvent?.Status !== AlertReviewStatus.Closed) {
            return;
        }
        const startTime = new Date(historyItem.StartTime);
        const endTime = new Date(historyItem.EndTime);

        return endTime.getTime() - startTime.getTime();
    }

    getDurationToResolveAlert(historyItem: IAlertReviewHistory) {
        if (!historyItem.latestAlertReviewEvent || historyItem.latestAlertReviewEvent?.Status !== AlertReviewStatus.Closed) {
            return;
        }
        const startTime = new Date(historyItem.StartTime);
        const endTime = new Date(historyItem.EndTime);
        return this.dateService.formatDuration(startTime, endTime);
    }


    getDurationInAlarmMs(historyItem: IAlertReviewHistory): number | null {
        if (!historyItem.AlertRecord?.TriggeredAtDataTimestamp && !historyItem.AlertRecord?.ClosedAtDataTime) {
            return null;
        }

        const startTime = new Date(historyItem.AlertRecord?.TriggeredAtDataTimestamp);
        const endTime = new Date(historyItem.AlertRecord?.ClosedAtDataTime);
        return endTime.getTime() - startTime.getTime();
    }


    getDurationInAlarm(historyItem: IAlertReviewHistory): string {
        if (!historyItem.AlertRecord?.ClosedAtDataTime) {
            return null;
        }
        const startTime = new Date(historyItem.AlertRecord.TriggeredAtDataTimestamp);
        const endTime = new Date(historyItem.AlertRecord.ClosedAtDataTime);
        return this.dateService.formatDuration(startTime, endTime);
    }

    getStartAndEndDates(historyItem: IAlertReviewHistory): { startTime: Date, endTime: Date } {

        const status = this.getCurrentStatusOfHistoryItem(historyItem);
        const startTime = new Date(historyItem.StartTime);
        let endTime: Date;

        if (status === AlertReviewStatus.Closed) {
            endTime = new Date(historyItem.EndTime);
        }
        else {
            endTime = new Date(); // Alert review is still ongoing
        }

        return {
            startTime,
            endTime
        };
    }

    getOpenDuration(historyItem: IAlertReviewHistory): number {
        let { startTime, endTime } = this.getStartAndEndDates(historyItem);

        const duration = endTime.getTime() - startTime.getTime();

        return duration;
    }

    updateAlertReviewHistoryItem(existingAlertHistory: IAlertReviewHistory[], alertHistory: IAlertReviewHistory): void {

        if (!existingAlertHistory?.length) {
            return;
        }

        if (!alertHistory?.Id) {
            return;
        }

        const indexOfExistingItem = existingAlertHistory.findIndex(history => history.Id === alertHistory.Id);

        if (indexOfExistingItem === -1) {
            return;
        }

        existingAlertHistory[indexOfExistingItem] = alertHistory;
    }
}
